import { Box, Container, Grid, Typography, Link as MUILink, Stack, IconButton } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";

import type { FooterProps, InstitutionalLink } from '../types';
import { theme } from '../constants/styles';
import LanguageIcon from "@mui/icons-material/Language";
const DEFAULT_INSTITUTIONAL_LINKS: InstitutionalLink[] = [
  { label: 'Conheça a Aviva Ingressos', href: '#' },
  { label: 'Termos de Uso', href: '#' },
  { label: 'Privacidade', href: '#' },
  { label: 'Cookies', href: '#' },
];

/**
 * Componente de rodapé reutilizável para páginas de eventos públicos
 * Exibe logo, descrição, links institucionais e redes sociais
 * 
 * @example
 * <Footer 
 *   logoUrl="/custom-logo.png"
 *   description="Texto customizado"
 *   instagramUrl="https://instagram.com/custom"
 * />
 */
export default function Footer({
 instagramUrl = "https://www.instagram.com/avivaexperiencias/",
  facebookUrl = "https://avivaingressos.com.br/",
  logoUrl = "/assets/logo/image 5.png",
  description = "Somos especialistas na organização de retiros, missões e viagens para Igrejas.",
  institutionalLinks = DEFAULT_INSTITUTIONAL_LINKS,
}: FooterProps) {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: theme.colors.primary,
        color: 'white',
        width: "100%",
        borderTopLeftRadius: theme.borderRadius.large,
        borderTopRightRadius: theme.borderRadius.large,
        mt: 4,
        py: { xs: 3, md: 6 },
        px: { xs: 2, md: 6 },
      }}
    >
      <Container maxWidth="lg" sx={{ px: 0 }}>
        <Grid container spacing={{ xs: 2, md: 5 }}>
          {/* Seção Esquerda - Logo e Descrição */}
          <Grid item xs={12} md={4}>
            <Stack
              sx={{
                alignItems: { xs: 'center', md: 'flex-start' },
                textAlign: { xs: 'center', md: 'left' },
                gap: { xs: 1.5, md: 3 },
              }}
            >
              {/* Logo */}
              <Box
                component="img"
                src={logoUrl}
                alt="Aviva Ingressos"
                sx={{
                  width: { xs: 150, md: 280 },
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />

              {/* Descrição */}
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontSize: { xs: '0.875rem', md: '1.0rem' },
                  lineHeight: 1.35,
                  maxWidth: 600,
                  fontWeight: 200,
                  fontFamily: theme.fonts.primary,
                }}
              >
                {description}
              </Typography>
            </Stack>
          </Grid>

          {/* Seção Meio - Institucional */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                color: 'white',
                fontSize: { xs: '0.9375rem', md: '1.25rem' },
                mb: { xs: 1.25, md: 3 },
                fontFamily: theme.fonts.primary,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Institucional
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                justifyContent: { xs: 'center', md: 'flex-start' },
                alignItems: { xs: 'center', md: 'flex-start' },
                columnGap: { xs: 1.5, md: 0 },
                rowGap: { xs: 0.75, md: 2 },
                gap: { md: 2 },
                px: { xs: 0.5, md: 0 },
              }}
            >
              {institutionalLinks.map((link, index) => (
                <MUILink
                  key={index}
                  href={link.href}
                  sx={{
                    color: 'white',
                    fontSize: { xs: '0.75rem', md: '1rem' },
                    textDecoration: 'none',
                    fontFamily: theme.fonts.primary,
                    textAlign: { xs: 'center', md: 'left' },
                    whiteSpace: { xs: 'nowrap', md: 'normal' },
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {link.label}
                </MUILink>
              ))}
            </Box>
          </Grid>

          {/* Seção Direita - Redes Sociais */}
          <Grid item xs={12} md={4}>
            <Stack
              sx={{
                alignItems: { xs: 'center', md: 'flex-start' },
                gap: { xs: 1.25, md: 0 },
              }}
            >
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                color: 'white',
                fontSize: { xs: '0.9375rem', md: '1.25rem' },
                mb: { xs: 0, md: 3 },
                fontFamily: theme.fonts.primary,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Nossas redes
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mb: { xs: 0, md: 4 }, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <IconButton
                component="a"
                href={instagramUrl}
                target="_blank"
                rel="noopener"
                sx={{
                  color: 'white',
                  border: '2px solid white',
                  width: { xs: 40, md: 56 },
                  height: { xs: 40, md: 56 },
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  '& svg': {
                    fontSize: { xs: '1.25rem', md: '1.75rem' },
                  },
                }}
                aria-label="Instagram"
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                component="a"
                href={facebookUrl}
                target="_blank"
                rel="noopener"
                sx={{
                  color: 'white',
                  border: '2px solid white',
                  width: { xs: 40, md: 56 },
                  height: { xs: 40, md: 56 },
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  '& svg': {
                    fontSize: { xs: '1.25rem', md: '1.75rem' },
                  },
                }}
                aria-label="Facebook"
              >
                <LanguageIcon />
              </IconButton>
            </Stack>
            </Stack>

          </Grid>
        </Grid>

        {/* Rodapé inferior */}
        <Box
          sx={{
            mt: { xs: 2, md: 5 },
            pt: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'center', md: 'space-between' },
            alignItems: 'center',
            gap: { xs: 0.75, md: 1.5 },
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'white',
              fontSize: { xs: '0.8125rem', md: '1rem' },
              fontFamily: theme.fonts.primary,
            }}
          >
            Feito com ❤️ e 🙏 para o público cristão
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: 'white',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontFamily: theme.fonts.primary,
            }}
          >
            ©{year} Aviva Ingressos. Todos os direitos reservados.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}